import type { BreadcrumbProps } from '@blueprintjs/core'
import { MenuDivider, MenuItem } from '../ui/menu'
import { Callout } from '../ui/callout'
import { Dialog } from '../ui/dialog'
import { observer } from 'mobx-react-lite'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Filters, PerformanceFilters } from '../../config'
import { useKdpFilters } from '../../hooks/useKdpFilters'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import { useUser } from '../../hooks/useUser'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import type { State } from '../../reducers'
import { userStore } from '../../stores/user_store'
import type { Campaign, SmartBidDryRun } from '../../types/campaign'
import type { EntityId } from '../../types/constants'
import { axiosGet } from '../../util/api'
import { adManagerPerformanceGet } from '../../util/adManagerPerformanceApi'
import { Layout } from '../Layout'
import {
    Columns,
    defaultPerfColumns,
    type ExpandedPerformanceMetrics,
    kdpPerfColumns,
} from '../table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable } from '../table2/AgGridTable'
import { TableStore } from '../table2/table_store'
import { useBidLimits } from '../bid_limits/useBidLimits'
import { SmartBidsTree } from '../trees/SmartBidsTree'
import { OverviewCharts } from '../../pages/dashboard-overview-charts'
import { normalizeAmazonPortfolioOptions } from '../ui/portfolioBulkActions'
import { campaignMutableFields } from './campaignMutableFields'
import { useAdManagerCustomFields } from '../../features/custom-fields/ad-manager'

const defaultFilters = [
    Filters.PortfolioId,
    Filters.State,
    Filters.DeliveryStatus,
    Filters.DeliveryReason,
    Filters.TargetingType,
    Filters.DailyBudget,
    Filters.SmartBid,
    Filters.TargetACOS,
    Filters.StartDate,
    Filters.EndDate,
    ...PerformanceFilters,
]

const mutableFields = campaignMutableFields

export const CampaignsTable = observer((): JSX.Element => {
    const useUserValues = useUser()
    const [smartBid, setSmartBid] = useState<SmartBidDryRun | null>(null)
    const [smartBidLoading, setSmartBidLoading] = useState(false)
    const filters = useKdpFilters(defaultFilters)
    const [store] = useState<TableStore<TempType>>(new TableStore(userStore, 'campaigns'))
    useBidLimits(store, 1, 'campaign_id')
    const savedFiltersStore = useSavedFiltersStore()
    const profile = useSelector<State, EntityId | null>((state) => state.user.profile)
    const load = (params: any) => {
        if (!profile) {
            return Promise.resolve()
        }

        return adManagerPerformanceGet('api/v5/campaigns', params, true)
    }

    const getPortfolios = () => {
        if (!profile) {
            return Promise.resolve()
        }

        return axiosGet(
            'api/v5/portfolios',
            {
                perPage: 1000,
                sort: 'name',
            },
            true,
        ).then(normalizeAmazonPortfolioOptions)
    }
    const tableHooks = useTableHooksFromStore(load, {}, true, store)
    const { params } = tableHooks
    const customFields = useAdManagerCustomFields<TempType>({
        profileId: profile === null ? null : String(profile),
        entityType: 'campaign',
        entityIdKey: 'campaign_id',
        store,
        reload: tableHooks.reload,
    })

    const fetchData = {
        portfolio: getPortfolios,
    }

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

    const cols3 = useMemo(() => {
        return [...newColumns(useUserValues.is_kdp), ...customFields.columns]
    }, [customFields.columns, useUserValues.is_kdp])

    return (
        <Layout
            breadcrumbs={breadcrumbs}
            primary="Campaigns"
            params={params}
            extraMenuItems={[
                <MenuItem
                    disabled={smartBidLoading}
                    onClick={() => {
                        setSmartBidLoading(true)
                        axiosGet(`campaigns/smart-bid-dry-run`).then((res) => {
                            setSmartBid(res.data)
                            setSmartBidLoading(false)
                        })
                    }}
                    text="Smart Bid Dry Run"
                />,
                <MenuDivider />,
            ]}
        >
            <OverviewCharts params={params} className="mb-4" chartType="campaigns" />
            <AgGridTable
                name={'Campaigns'}
                columns={cols3}
                tableHooks={tableHooks}
                store={store}
                showHeader={false}
                savedFiltersStore={savedFiltersStore}
                filters={filters}
                selectKey="campaign_id"
                stableRowId
                search
                searchPlaceholder="Search by campaign"
                columnSelection
                mutableFields={mutableFields}
                bulkActions
                otherActionsLeft={customFields.bulkAction}
                fetchData={fetchData}
            />
            <Dialog
                style={{ minWidth: 1200 }}
                className="p-4"
                isOpen={!!smartBid}
                onClose={() => setSmartBid(null)}
            >
                <Callout intent="primary" className="mb-3">
                    This is a preview of what <i>would have</i> been changed if you had smart bids
                    enabled. Smart bids automatically adjust your bid in order to get your ACoS
                    running as close as possible to your target ACoS. Smart bids run once every day,
                    immediately after one of your data updates.
                </Callout>

                {smartBid?.keywords && (
                    <div>
                        <SmartBidsTree title={'Keywords'} data={smartBid.keywords} />
                    </div>
                )}

                {smartBid?.targets && (
                    <div>
                        <SmartBidsTree title={'Targets'} data={smartBid.targets} type="targets" />
                    </div>
                )}
            </Dialog>
        </Layout>
    )
})

export type TempType = Campaign & ExpandedPerformanceMetrics

const newColumns = <T,>(isKdp: boolean): AgGridColumnDef<T>[] => {
    const cols: AgGridColumnDef<T>[] = [
        Columns.Select,
        Columns.CampaignName,
        Columns.SmartBidToggle,
        Columns.StartDate,
        Columns.EndDate,
        Columns.ProductAdsCount,
        Columns.DailyBudget,
        Columns.LastDailyBudgetChange,
        Columns.BidLimits,
        ...defaultPerfColumns,
    ]

    spliceAfter('bid_limits', cols, Columns.BidStrategy)
    spliceAfter('bid_strategy', cols, Columns.BidAdjustments)
    spliceAfter('name', cols, Columns.DeliveryStatus)

    addExtraCols(cols, isKdp)

    return cols
}

export const addExtraCols = <T,>(cols: AgGridColumnDef<T>[], isKdp: boolean) => {
    // Add Target CPC just before the CPC column
    const cpcIndex = cols.findIndex((col) => col.field === 'cpc')
    if (cpcIndex !== -1) {
        cols.splice(cpcIndex, 0, Columns.TargetCPC)
    }

    // Add Target ACOS just before the ACOS column
    const acosIndex = cols.findIndex((col) => col.field === 'acos')
    if (acosIndex !== -1) {
        cols.splice(acosIndex, 0, Columns.TargetACOS)
    }

    // Optionally add KDP columns
    if (isKdp) {
        cols.push(...kdpPerfColumns)

        // Splice in the adjusted columns
        spliceAfter('orders', cols, Columns.AdjustedOrders)
        spliceAfter('sales', cols, Columns.AdjustedSales)
        spliceAfter('adjusted_sales', cols, Columns.BlendedProfit)
        spliceAfter('acos', cols, Columns.BlendedACOS)
        spliceAfter('roas', cols, Columns.BlendedROAS)
        spliceAfter('cvr', cols, Columns.BlendedCVR)
        spliceAfter('cac', cols, Columns.BlendedCAC)
        spliceAfter('aov', cols, Columns.BlendedAOV)
        spliceAfter('rpc', cols, Columns.BlendedRPC)
        spliceAfter('pages_read', cols, Columns.AdjustedPagesRead)
        spliceAfter('estimated_royalties', cols, Columns.AdjustedRoyalties)
    }
}

export const spliceAfter = <T,>(
    columnName: string,
    columns: AgGridColumnDef<T>[],
    col: AgGridColumnDef<T>,
) => {
    const index = columns.findIndex((col) => col.field === columnName)
    if (index == -1) {
        console.error(`spliceAfter: columnName ${columnName} not found in columns`)
        return
    }
    columns.splice(index + 1, 0, col)
}
